import ButtonOutline from "kreate-common/modules/kreate-ui/components/ButtonOutline";
import ButtonPrimary from "kreate-common/modules/kreate-ui/components/ButtonPrimary";
import Divider from "kreate-common/modules/kreate-ui/components/Divider";
import Flex from "kreate-common/modules/kreate-ui/components/Flex";
import ModalV2 from "kreate-common/modules/kreate-ui/components/ModalV2";
import React from "react";
import { MdCheck } from "react-icons/md";

import { AssetViewer$Ada$Compact } from "../../../kreate-components/components/AssetViewer$Ada$Compact";
import { useModalTrigger$ModalConfirm } from "../ModalConfirm/hooks";

import ButtonPopoverInfo from "./components/ButtonPopoverInfo";
import FieldMintingMethod from "./fields/FieldMintingMethod";
import {
  MintingMethod,
  useFieldMintingMethod,
} from "./fields/FieldMintingMethod/hooks";
import FieldMintPrice from "./fields/FieldMintPrice";
import { useFieldMintPrice } from "./fields/FieldMintPrice/hooks";
import FieldNumEditions from "./fields/FieldNumEditions";
import { useFieldNumEditions } from "./fields/FieldNumEditions/hooks";
import FieldNumMinting from "./fields/FieldNumMinting";
import { useFieldNumMinting } from "./fields/FieldNumMinting/hooks";
import styles from "./index.module.scss";

import { InlineError } from "@/modules/form-field/errors";
import { useToast } from "@/modules/kreate-contexts/ToastContext";

type SuccessEvent = {
  mintPrice: number;
  numEditions: number;
  numMinting: number;
  mintingMethod: MintingMethod;
};

export type ModalEnableMinting$Prompt$SuccessEvent = SuccessEvent;

type UseCase = "create" | "update";

type Props = {
  useCase: UseCase;
  open: boolean;
  initialMintPrice?: number;
  initialNumEditions?: number;
  onSuccess: (event: SuccessEvent) => void;
  onCancel: () => void;
};

export default function ModalEnableMinting$Prompt({
  useCase,
  open,
  initialMintPrice,
  initialNumEditions,
  onSuccess,
  onCancel,
}: Props) {
  const { showError } = useToast();
  const modalTriggers = {
    ModalConfirm: useModalTrigger$ModalConfirm(),
  };
  const fieldMintPrice = useFieldMintPrice({ initialMintPrice });
  const fieldNumEditions = useFieldNumEditions({ initialNumEditions });
  const fieldNumMinting = useFieldNumMinting({
    numEditions: fieldNumEditions?.value,
  });
  const fieldMintingMethod = useFieldMintingMethod({
    initialValue: "on-demand-minting",
  });

  const handleSubmit = async () => {
    try {
      const formData = {
        numEditions: fieldNumEditions.validateBeforeSubmitting(),
        mintPrice: fieldMintPrice.validateBeforeSubmitting(),
        // We do not support minting while updating for now
        numMinting:
          useCase === "create" ? fieldNumMinting.validateBeforeSubmitting() : 0,
        mintingMethod: fieldMintingMethod.validateBeforeSubmitting(),
      };
      if (formData.numEditions === formData.numMinting) {
        const confirmed = await modalTriggers.ModalConfirm.openModal(
          <div>
            <span>{"Hold on! Entering "}</span>
            <em>{"everything"}</em>
            <span>
              {
                " into For Yourself means all the Editions will be minted to your wallet; "
              }
            </span>
            <span>
              {
                "Collectors won't be able to pick any up! Are you sure you want to continue?"
              }
            </span>
            <span>
              {
                " If you want every Edition to sell on the market instead, please enter 0."
              }
            </span>
          </div>
        );
        if (!confirmed) {
          return;
        }
      }
      onSuccess(formData);
    } catch (error) {
      if (error instanceof InlineError) {
        return;
      } else {
        showError({ error });
      }
    }
  };

  // TODO: More precise calculations with Helios and protocol params
  const estimatedFee =
    fieldNumMinting.value != null
      ? Math.max(fieldNumMinting.value * 500_000, 2_000_000) + 1_950_000
      : null;

  return (
    <ModalV2 open={open} onClose={onCancel}>
      <ModalV2.Header
        content={
          useCase === "create" ? "Enable Minting" : "Update Minting Details"
        }
      />
      <ModalV2.Content>
        <Flex.Col gap="32px">
          <FieldNumEditions api={fieldNumEditions} />
          <FieldMintPrice api={fieldMintPrice} />
          {useCase === "create" && (
            <>
              <Divider.Horizontal />
              <div className={styles.textMintToYours}>{`For Yourself`}</div>
              <FieldNumMinting api={fieldNumMinting} />
              <FieldMintingMethod api={fieldMintingMethod} />
            </>
          )}
        </Flex.Col>
      </ModalV2.Content>
      <ModalV2.Actions>
        {useCase === "create" && (
          <Flex.Col flex="1 1 auto">
            <div className={styles.estimationLabel}>{"Estimated fee"}</div>
            <Flex.Row className={styles.estimationContent} gap="8px">
              <AssetViewer$Ada$Compact
                as="span"
                lovelaceAmount={estimatedFee}
                approx
              />
              <ButtonPopoverInfo content="We charge 0.5 ADA per Edition minted in this initial transaction, with a minimum fee of 2 ADA. The rest are blockchain network fees." />
            </Flex.Row>
          </Flex.Col>
        )}
        <ButtonOutline size="xl" content="Cancel" onClick={onCancel} />
        <ButtonPrimary
          size="xl"
          icon={<MdCheck />}
          content="Submit"
          onClick={handleSubmit}
        />
      </ModalV2.Actions>
    </ModalV2>
  );
}
